// The app shell's layout state, kept as attributes on <html> and styled with the Tailwind
// variants in app.css (`nav-collapsed:`, `chat-docked:`, …). While `data-instant` is set, layout
// changes apply without animating.

const root = document.documentElement;
// The narrowest a page gets beside an expanded sidebar (px); a page with the AI chat can set its
// own (`data-page-min-width`). Below `lg` the sidebar is a sheet, so this only applies from there.
const PAGE_MIN_WIDTH = 960;
const LG = 1024;
// The framed page card's inset from the window's right edge (`main`'s `lg:framed:pr-2`), unless
// the chat docks there. The flat shell has none.
const rightInset = () => (document.body.hasAttribute('data-flat-layout') ? 0 : 8);

const px = name => {
  const value = getComputedStyle(root).getPropertyValue(name);
  return parseFloat(value) * (/rem/.test(value) ? 16 : 1);
};

// Below `sm` the AI chat covers the whole screen, so a page starts with it closed there whatever
// the saved preference; the launcher still opens it.
export const isPhone = () => window.matchMedia('(max-width: 639px)').matches;

// Whether the user collapsed the sidebar, remembered in this browser. A cookie, so the server
// renders the page that way from the start (`NavComponent.sidebar_collapsed?/1`).
const saveSidebarCollapsed = collapsed => {
  document.cookie = `sidebar_collapsed=${collapsed ? 1 : 0}; path=/; max-age=31536000; samesite=lax`;
};

// Docks the AI chat beside the page when there's room, or else floats it over the page; and
// collapses the sidebar to its rail when the page would otherwise get narrower than its minimum
// (beside the docked chat, if it's open).
export function updateLayout() {
  const host = document.querySelector('[data-assistant-host]');
  const width = window.innerWidth;
  const pageMin = Number(host?.dataset.pageMinWidth) || PAGE_MIN_WIDTH;
  let besidePage = rightInset();

  if (host) {
    const column = px('--app-assistant-expanded-width');
    // A widened column never makes the chat float: at most its default width counts.
    const floating = width < LG || width - 60 - Math.min(column, 400) < pageMin;
    root.setAttribute('data-assistant-mode', floating ? 'floating' : 'docked');
    if (!floating && root.hasAttribute('data-assistant-open')) besidePage = column;
  } else {
    root.removeAttribute('data-assistant-mode');
  }

  const squeeze =
    width >= LG &&
    !root.hasAttribute('data-sidebar-pinned') &&
    width - px('--app-sidebar-expanded-width') - besidePage < pageMin;

  root.toggleAttribute('data-sidebar-auto-collapsed', squeeze);
}

// Expanding a sidebar that collapsed on its own pins it open (until the chat closes, or the next
// page load).
function toggleSidebar() {
  if (root.hasAttribute('data-sidebar-auto-collapsed')) {
    root.setAttribute('data-sidebar-pinned', '');
    root.removeAttribute('data-sidebar-collapsed');
    saveSidebarCollapsed(false);
    updateLayout();
    return;
  }

  if (root.hasAttribute('data-sidebar-pinned')) {
    root.removeAttribute('data-sidebar-pinned');
    updateLayout();
    if (root.hasAttribute('data-sidebar-auto-collapsed')) return;
  }

  saveSidebarCollapsed(root.toggleAttribute('data-sidebar-collapsed'));
}

export function initLayout() {
  // The page is already styled by now: set it up without animating into place.
  root.setAttribute('data-instant', '');

  if (isPhone()) root.removeAttribute('data-assistant-open');
  updateLayout();
  void root.offsetWidth;
  root.removeAttribute('data-instant');

  window.addEventListener('resize', updateLayout);
  window.addEventListener('app:toggle-sidebar', toggleSidebar);
  window.addEventListener('phx:app:set-layout', setLayout);
}

// The organization's layout changed in its settings: apply it without a reload.
function setLayout({ detail }) {
  document.body.toggleAttribute('data-flat-layout', detail.flat);
  updateLayout();
}
